import CardCommon from '@op/components/CardCommon';
import GPT from '@op/components/GPT';
import useLanguage from '@op/hooks/useLanguage';
import { useRealTimeStreamingSummary } from '@op/pages/RealtimeStreaming/hooks/useRealTimeStreamingSummary';
import { LiveTimeType } from '@op/pages/RealtimeStreaming/type';
import { getLiveTimeTitle } from '@op/pages/RealtimeStreaming/utils';
import React, { useEffect, useState } from 'react';
import { Button, Justify, Segment, TabPanel, Tabs, Tag } from 'tea-component';
import KolQuotesModal, { SummaryItem } from './KolQuotesModal';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
import './index.scss';
import { DBO_POINT } from '@op/operationLog/buriedPoint';
import TimeRangeSelect from './TimeRange';

const tabList = [
  { text: 'Summary by 5-minutes intervals', value: 'five' },
  { text: 'Overall summary for the selected time period', value: 'all' },
];

export default function LiveStreamingContentAnalysis({
  filter,
  liveTime,
  hasLiveCharts,
  setFilterTime,
  timeFilterValue,
  setTimeFilterValue,
  realLiveTime,
}: {
  filter: any;
  liveTime?: LiveTimeType;
  hasLiveCharts?: boolean;
  setFilterTime: (value: { startTime: string; endTime: string }) => void;
  timeFilterValue?: { startTime: string; endTime: string };
  setTimeFilterValue: (value: { startTime: string; endTime: string }) => void;
  realLiveTime?: string;
}) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [value, setValue] = useState('five');
  const [visible, setVisible] = useState(false);
  const { loading, topicList, list } = useRealTimeStreamingSummary({ filter });
  const { summaryRef, getSummary } = useGeySummary({ isPreviews: false });

  useEffect(() => {
    if (value === 'all') {
      setTimeout(() => {
        getSummary({ prompt: '------------' });
      }, 10);
    }
  }, [filter, value]);

  return (
    <CardCommon
      className="dbo-live-charts-analysis-wrapper"
      title={
        <div className="dbo-bubble-common-title">
          {formatMessage('Live Streaming Content Analysis')}
          {/* <BubbleCommon tipsMap={undefined}></BubbleCommon> */}
          {liveTime && (
            <span className="dbo-common-subtitle">
              ({formatMessage('Analysis Time Range')}:{' '}
              {getLiveTimeTitle(liveTime)})
            </span>
          )}
        </div>
      }
    >
      <>
        {!hasLiveCharts && (
          <TimeRangeSelect
            setFilterTime={setFilterTime}
            timeFilterValue={timeFilterValue!}
            setTimeFilterValue={setTimeFilterValue}
          ></TimeRangeSelect>
        )}
        {Object.keys(topicList).length > 1 && (
          <CardCommon
            className="dbo-live-charts-analysis-related"
            title={formatMessage('Related Topics')}
            loading={loading}
            loadingHeight={100}
            contentCard
          >
            <div className="dbo-tea-tag-list">
              {Object.entries(topicList).map(([key, value]) => (
                <Tag key={key} className={`dbo-tea-tag dbo-tea-tag--${value}`}>
                  {key}
                </Tag>
              ))}
            </div>
          </CardCommon>
        )}
        <Justify
          left={
            <Segment
              value={value}
              className="dbo-live-charts-analysis-segment"
              onChange={(value) => {
                setValue(value);
              }}
              options={formatOptionsMessage(tabList)}
            />
          }
          right={
            <Button
              onClick={() => {
                setVisible(true);
                DBO_POINT.btn('ViewKOLQuotes');
              }}
              type="link"
            >
              {formatMessage('View KOL Quotes')}
            </Button>
          }
        ></Justify>

        <Tabs
          activeId={value}
          className="dbo-hide-tabs"
          tabs={tabList.map(({ text, value }) => ({ label: text, id: value }))}
        >
          <TabPanel id={'five'}>
            <CardCommon
              className="dbo-live-charts-analysis-cardCommon"
              empty={list.length === 0}
              emptyHeight={250}
              contentCard
              loading={loading}
              loadingHeight={250}
            >
              {list.map((item, index) => (
                <SummaryItem
                  key={item.start_time + item.end_time + index}
                  data={item}
                ></SummaryItem>
              ))}
            </CardCommon>
          </TabPanel>
          <TabPanel id={'all'}>
            <GPT.SteamingPageCard
              className="gpt-summary gpt-summary--realtime-streaming  --hasStopFalse"
              oriFeedsState={{ total: 100 }}
              filter={filter}
              ref={summaryRef}
              hasStopBtn={false}
              autoRequest={false}
              hoverNoShadow
              isMini
              showModelType
              hasTotalText={false}
              hasResultOutput={false}
              hasAdvancedSummary={false}
              hasAgent={false}
              needRequestPrompt={false}
              previewPrompt
            >
              {(toggleVisible: any, { loading, total }: any) => {
                return (
                  <div className="gpt-summary-button">
                    <GPT.Button
                      disabled={total <= 0}
                      loading={false}
                      onClick={() => {
                        toggleVisible();
                        DBO_POINT.btn('CreateAllTimeSummary');
                      }}
                    ></GPT.Button>
                  </div>
                );
              }}
            </GPT.SteamingPageCard>
          </TabPanel>
        </Tabs>
        {visible && (
          <KolQuotesModal
            filter={filter}
            liveTime={realLiveTime!}
            close={() => setVisible(false)}
          ></KolQuotesModal>
        )}
      </>
    </CardCommon>
  );
}
